import { SelectBox } from "devextreme-react/select-box";
import { ValidationGroup } from "devextreme-react/validation-group";
import { ValidationSummary } from "devextreme-react/validation-summary";
import { RequiredRule, Validator } from "devextreme-react/validator";
import Box, { Item } from "devextreme-react/box";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import { useContext, useMemo, useState } from "react";
import { useAuth } from "../../../../contexts/auth";
import { MOType, SalesChannels, StockingOrderWorkflow } from "../../../../types/enums";
import { useGetROLinkCandidates } from "../api/get-ro-link-candidates";
import { MaterialOrderContext } from "../context/MaterialOrderContext";
import { ROLinkCandidate } from "../types/ROLinkCandidate";
import { useGetSalesChannels } from "../api/get-sales-channels";

export default function ROLinkPopup(props: {
	visible: boolean,
	onPopupHidden: (result: boolean) => void
}) {

	const { order, modifyOrder } = useContext(MaterialOrderContext);
	const isBlockedWorkflow = order.details.stockingOrderWorkflowID === StockingOrderWorkflow.Blocked;
	const [linkToExistingRO, setLinkToExistingRO] = useState(isBlockedWorkflow);
	const [matLink, setMatLink] = useState(true);
	const [finLink, setFinLink] = useState(true);
	const [salesChannelID, setSalesChannel] = useState();
	const [retailOrder, setRetailOrder] = useState<ROLinkCandidate>();
	const { user } = useAuth();

	const retailOrders = useGetROLinkCandidates(user?.companyId!, props.visible);
	const salesChannels = useGetSalesChannels(user?.companyId!);

	const stockingSalesChannels = useMemo(() => {
		return salesChannels.data ? salesChannels.data.filter(x => x.salesChannelName.match(/stocking|other/i)) : [];
	}, [salesChannels.data]);

	return (
		<ValidationGroup>
			<Popup
				title="Link Order"
				visible={props.visible}
				hideOnOutsideClick={false}
				width={600}
				height={400}
			>
				<ToolbarItem
					options={{
						icon: 'dx-icon dx-icon-close',
						onClick: function () {
							props.onPopupHidden(false);
						}
					}}
					widget="dxButton"
					location="after"
				/>
				<div className="linked-ro-popup-content">
					<ValidationSummary validationGroup="linkOrderValidationGroup" />

					<Box visible={!isBlockedWorkflow}>
						<Item ratio={1}>
							Do you wish to link this Material Order to an existing Retail Order?
						</Item>
						<Item ratio={0} baseSize={100}>
							<SelectBox
								items={[{ text: 'Yes', value: true }, { text: 'No', value: false }]}
								valueExpr='value'
								displayExpr='text'
								showClearButton={false}
								value={linkToExistingRO}
								onValueChanged={(e) => setLinkToExistingRO(e.value)}
							>
							</SelectBox>
						</Item>
					</Box>

					<Box visible={isBlockedWorkflow}>
						<Item ratio={1}>
							Please select the RO that you are placing a material order for. The selected RO
							should be greater than the value of the material order you are placing.
						</Item>
					</Box>

					<Box visible={!linkToExistingRO}>
						<Item ratio={1}>
							Will stocking order be used for?
						</Item>
						<Item ratio={0} baseSize={250}>
							<SelectBox
								items={stockingSalesChannels}
								valueExpr='salesChannelID'
								displayExpr='salesChannelName'
								showClearButton={false}
								value={salesChannelID}
								onValueChanged={(e) => setSalesChannel(e.value)}
							>
								<Validator validationGroup="linkOrderValidationGroup">
									{!linkToExistingRO &&
										<RequiredRule message={'Sales Channel must be selected'} />
									}
								</Validator>
							</SelectBox>
						</Item>
					</Box>

					<Box visible={linkToExistingRO}>
						<Item ratio={1}>
							Retail Order:
						</Item>
						<Item ratio={0} baseSize={120}>
							<SelectBox
								dataSource={retailOrders.data}
								valueExpr='retailOrderID'
								displayExpr='roNumber'
								showClearButton={false}
								searchEnabled={true}
								value={retailOrder?.retailOrderID}
								onValueChanged={(e) => setRetailOrder(retailOrders.data?.find((x) => x.retailOrderID === e.value))}
							>
								<Validator validationGroup="linkOrderValidationGroup">
									{linkToExistingRO &&
										<RequiredRule message={'Retail Order must be selected'} />
									}
								</Validator>
							</SelectBox>
						</Item>
					</Box>

					<Box visible={linkToExistingRO}>
						<Item ratio={1}>
							Will Material Order be used to complete this Order?
						</Item>
						<Item ratio={0} baseSize={100}>
							<SelectBox
								items={[{ text: 'Yes', value: true }, { text: 'No', value: false }]}
								valueExpr='value'
								displayExpr='text'
								showClearButton={false}
								value={matLink}
								onValueChanged={(e) => setMatLink(e.value)}
								readOnly={isBlockedWorkflow}
							>
							</SelectBox>
						</Item>
					</Box>

					<Box visible={linkToExistingRO}>
						<Item ratio={1}>
							Will Material Order be paid from this job's proceeds?
						</Item>
						<Item ratio={0} baseSize={100}>
							<SelectBox
								items={[{ text: 'Yes', value: true }, { text: 'No', value: false }]}
								valueExpr='value'
								displayExpr='text'
								showClearButton={false}
								value={finLink}
								onValueChanged={(e) => setFinLink(e.value)}
								readOnly={isBlockedWorkflow}
							>
							</SelectBox>
						</Item>
					</Box>
				</div>

				<ToolbarItem
					options={{
						validationGroup: 'linkOrderValidationGroup',
						text: 'OK',
						onClick: (e: any) => {

							let validation = e.validationGroup.validate();
							if (validation.isValid) {
								modifyOrder({
									...order.details,
									retailOrderID: linkToExistingRO && matLink ? retailOrder?.retailOrderID : undefined,
									roDate: linkToExistingRO ? retailOrder?.roDate : undefined,
									matLink: linkToExistingRO ? matLink : false,
									finLink: linkToExistingRO ? finLink : false,
									salesChannelID: linkToExistingRO ? retailOrder?.salesChannelID! : salesChannelID!,
									moTypeID: !linkToExistingRO || !matLink ? MOType.Stocking : order.details.moTypeID
								});

								props.onPopupHidden(true);
							}
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				>
				</ToolbarItem>
				<ToolbarItem
					options={{
						text: 'Cancel',
						onClick: () => props.onPopupHidden(false)
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				>
				</ToolbarItem>

			</Popup>
		</ValidationGroup>
	)
}
